/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { User } from '@app/settings/users/user.model';
import { UsersService } from '@app/settings/users/users.service';
import { RolesService } from '@app/settings/users/roles/roles.service';
import { ApiQuery } from '@app/core/http/api-query';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { UserRole } from '@app/settings/users/roles/user-role.model';
import { AuthenticationService, Logger } from '@app/core';
import { FileUpload } from 'primeng/fileupload';
import { ContactComponent } from '@app/shared/components/contact/contact.component';
import { BankAccountsComponent } from '@app/shared/components/bank-accounts/bank-accounts.component';
import { DomSanitizer } from '@angular/platform-browser';
import { MediaLibraryAcceptTypes } from '@app/shared/components/media-library/media-library-accept-types.enum';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';
import { SmtpSettingsComponent } from '@app/shared/components/smtp-settings/smtp-settings.component';
import { AccessService } from '@app/core/access/access.service';
import { environment } from '@env/environment';
import { UsersEnum } from '@app/settings/users/users.enum';
import { ConfirmService } from '@app/shared/services/confirm.service';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

const log = new Logger('UsersFormComponent');

@Component({
  selector: 'app-users-form',
  templateUrl: './users-form.component.html',
  styleUrls: ['./users-form.component.scss'],
})
export class UsersFormComponent implements OnInit {
  @Input() user: User = null;
  form: FormGroup;
  roles: Array<UserRole> = [];
  conditionalRequiredFields: Array<string> = [
    'egn',
    'birthdate',
    'gender',
    'working_time_from',
    'working_time_to',
    'lunch_break',
    'city_id',
    'salary_payment_method_id',
    'contract_start_date',
    'company_id',
    'base_salary_amount',
    'working_hours_per_week',
  ];
  conditionalRequiredSmtpFields: Array<string> = [
    'smtp_settings_host',
    'smtp_settings_port',
    'smtp_settings_username',
    'smtp_settings_password',
    'smtp_settings_encryption',
    'smtp_settings_from_email',
    'smtp_settings_from_name',
  ];
  @ViewChild('fileUpload', { static: true }) fileUpload: FileUpload;
  @ViewChild(ContactComponent) contactComponent: ContactComponent;
  MediaLibraryAcceptTypes = MediaLibraryAcceptTypes;

  UserRoleType = UserRoleType;

  env = environment;
  UsersEnum = UsersEnum;

  constructor(
    public modal: NgbActiveModal,
    public authenticationService: AuthenticationService,
    private formBuilder: FormBuilder,
    private usersService: UsersService,
    private rolesService: RolesService,
    private sanitizer: DomSanitizer,
    public accessService: AccessService,
    private confirmService: ConfirmService
  ) {}

  ngOnInit() {
    if (this.user) {
      this.user = Object.assign({}, new User().deserialize(this.user));
      this.user.roles = [];
    }

    this.createForm();
    this.patchForm();
    setTimeout(() => {
      this.loadRoles();
    }, 1000);
  }

  loadRoles() {
    const query = new ApiQuery().setLimit(9999);
    this.rolesService.index(query).subscribe((response: PaginatedResponse) => {
      this.roles = response.data;
      const formControls = this.roles.map((item: UserRole) => {
        return this.formBuilder.group({
          id: item.id,
          name: item.name,
          selected:
            this.user && this.user.roles ? this.user.roles.some((role: UserRole) => item.id === role.id) : false,
        });
      });
      this.form.controls.roles = new FormArray(formControls);
    });
  }
  number(number: any): number {
    return Number(number);
  }

  addAvatar(event: any) {
    const reader = new FileReader();
    const [file] = event.files;
    reader.readAsDataURL(file);

    reader.onload = () => {
      this.form.patchValue({
        new_avatar: reader.result,
      });

      this.fileUpload.files = [];
    };
  }

  clearAvatar() {
    this.fileUpload.files = [];
    this.form.get('avatar').setValue(null);
    this.form.get('new_avatar').setValue(null);
  }

  save() {
    const checks = [];

    if (this.contactComponent) {
      checks.push(this.contactComponent.checkAllContactsExists());
    }

    if (checks.length > 0) {
      forkJoin(checks)
        .pipe(map((results) => results.some((r) => !!r)))
        .subscribe((hasDuplicates) => {
          if (hasDuplicates) {
            this.confirmService.confirm('Duplicates found. Save anyway?', 'Duplicate contacts').subscribe((ok) => {
              if (ok) {
                this.performSave();
              }
            });
          } else {
            this.performSave();
          }
        });
    } else {
      this.performSave();
    }
  }

  private performSave() {
    // това е под въпрос
    if (!this.form.get('employee').value) {
      this.conditionalRequiredFields.forEach((field: string) => {
        delete this.form.value[field];
      });
    }

    const user = new User().deserialize(this.form.value);

    user.roles = this.form
      .get('roles')
      .value.filter((role: any) => role.selected)
      .map((role: any) => {
        return { id: role.id };
      });

    if (this.user && this.user.id) {
      if (user.password === '') {
        user.password = null;
      }

      /*
      Проверка дали има аватар за качване?
       */
      if (!user.new_avatar) {
        delete user.new_avatar;
      }

      this.usersService.update(user).subscribe((res) => this.modal.close(res));
    } else {
      this.usersService.store(user).subscribe((res) => this.modal.close(res));
    }
  }

  /**
   *  Променя задължителните полета във формата отнасящи се за employee
   */
  employeeBoolChange() {
    this.conditionalRequiredFields.forEach((field: string) => {
      if (this.form.get('employee').value) {
        this.form.get(field).setValidators([Validators.required]);
      } else {
        this.form.get(field).setValidators([]);
      }
      this.form.get(field).updateValueAndValidity();
    });
  }

  /**
   * чупи адреса без това
   *  https://stackoverflow.com/questions/46041210/angular-2-ngstyle-and-background-image
   * @param imgStr img
   */
  getBackgroundImg(imgStr: string) {
    const imageString = JSON.stringify(imgStr).replace(/\\n/g, '');
    const style = 'url(' + imageString + ')';
    return this.sanitizer.bypassSecurityTrustStyle(style);
  }

  log() {
    log.debug(this.form);
  }

  private createForm() {
    let passwordValidator: any = [Validators.minLength(6)];
    if (!this.user || !this.user.id) {
      passwordValidator = [Validators.required, Validators.minLength(6)];
      log.debug('validate', passwordValidator);
    }
    this.form = this.formBuilder.group({
      id: [null],
      title_id: [null, [Validators.required]],
      first_name: [null],
      middle_name: [null],
      last_name: [null],
      email: [null, [Validators.required]],
      password: [null, passwordValidator],
      contacts: new FormArray([]),
      bank_accounts: new FormArray([]),
      roles: new FormArray([]),
      employee: [false, [Validators.required]],
      company_id: [null],
      fake_first_name: [null, [Validators.required]],
      fake_last_name: [null, [Validators.required]],
      fake_email: [null, [Validators.required]],
      address: [null],
      address2: [null],
      post_code: [null],
      egn: [null],
      birthdate: [null],
      gender: [null],
      working_time_from: [null],
      working_time_to: [null],
      lunch_break: [null],
      city_id: [null],
      city: [null],
      salary_payment_method_id: [null],
      contract_start_date: [null],
      contract_end_date: [null],
      base_salary_amount: [null],
      working_hours_per_week: [null],
      avatar: [null],
      new_avatar: [null],
      locales: [[]],
      companies: [[]],
      allowed_ip_addresses: [[]],
      active: [true],
      enable_notifications: [true],
      smtp_settings: new FormArray([]),
    });
  }

  private patchForm() {
    log.debug('this.user patch', this.user);
    if (this.user && this.user.id) {
      const query = new ApiQuery().addIncludes(
        'city.country',
        'locales',
        'contacts',
        'bank_accounts',
        'companies',
        'avatar',
        'roles'
      );
      this.usersService.show(this.user.id, query).subscribe((user: User) => {
        this.user = user;

        log.debug('patch user get');

        if (user['contacts'].length > 0) {
          this.form.setControl('contacts', ContactComponent.createMany(user['contacts']));
        }
        if (user['bank_accounts'].length > 0) {
          this.form.setControl('bank_accounts', BankAccountsComponent.createMany(user['bank_accounts']));
        }

        if (user['smtp_settings'].length > 0) {
          this.form.setControl('smtp_settings', SmtpSettingsComponent.createMany(user['smtp_settings']));
        }

        this.form.patchValue(user);

        this.form.updateValueAndValidity();
      });
    } else if (this.user) {
      this.form.patchValue(this.user);
    }
  }
}
